import { useState, useEffect, useRef } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { adminApi, SERVER_BASE_URL } from "services/api";
import { lightDark, DARK } from "utils/lightDark";

const INLINE_CODE_SX = {
  fontFamily: "monospace",
  background: lightDark("#f5f5f5", DARK.surfaceHover),
  padding: "1px 5px",
  borderRadius: "3px",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function mimeIcon(mimeType) {
  if (!mimeType) return "insert_drive_file";
  if (mimeType === "application/pdf") return "picture_as_pdf";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "videocam";
  if (mimeType.startsWith("audio/")) return "audiotrack";
  if (mimeType.includes("zip") || mimeType.includes("compressed")) return "folder_zip";
  if (mimeType.includes("word") || mimeType.includes("document")) return "description";
  if (mimeType.includes("sheet") || mimeType.includes("excel")) return "table_chart";
  return "insert_drive_file";
}

function mimeColor(mimeType) {
  if (!mimeType) return "secondary";
  if (mimeType === "application/pdf") return "error";
  if (mimeType.startsWith("image/")) return "info";
  if (mimeType.startsWith("video/")) return "warning";
  return "secondary";
}

function slugify(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

// Small icon buttons (30px) and small buttons (32px) get a comfortable tap size on touch screens.
const phoneIconTap = ({ breakpoints }) => ({ [breakpoints.down("md")]: { p: 1 } });
const phoneTapHeight = ({ breakpoints }) => ({ [breakpoints.down("md")]: { minHeight: 36 } });
// Usage-hint chips wrap onto a second line on phones instead of truncating.
const phoneChipWrap = ({ breakpoints }) => ({
  [breakpoints.down("md")]: {
    height: "auto",
    py: 0.5,
    "& .MuiChip-label": { whiteSpace: "normal" },
  },
});

// ─── Copy helper ──────────────────────────────────────────────────────────────

function useCopied() {
  const [copiedKey, setCopiedKey] = useState(null);
  const copy = (text, key) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1800);
  };
  return { copiedKey, copy };
}

// ─── File card ────────────────────────────────────────────────────────────────

function FileCard({ asset, onDelete, onCopy, copiedKey }) {
  const templateVar = `{{file:${asset.slug}}}`;
  const publicUrl = `${SERVER_BASE_URL}/files/${asset.slug}`;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "divider",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 4 },
      }}
    >
      <CardContent sx={{ flex: 1, pb: 0 }}>
        {/* Icon */}
        <MDBox display="flex" justifyContent="center" mb={1.5}>
          <MDBox
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${mimeColor(asset.mimeType)}.light`,
            }}
          >
            <Icon sx={{ fontSize: 32, color: `${mimeColor(asset.mimeType)}.main` }}>
              {mimeIcon(asset.mimeType)}
            </Icon>
          </MDBox>
        </MDBox>

        {/* Label / filename */}
        <MDTypography
          variant="subtitle2"
          fontWeight="medium"
          textAlign="center"
          noWrap
          title={asset.label || asset.originalFilename}
        >
          {asset.label || asset.originalFilename}
        </MDTypography>
        {asset.label && (
          <MDTypography
            variant="caption"
            color="secondary"
            display="block"
            textAlign="center"
            noWrap
            title={asset.originalFilename}
          >
            {asset.originalFilename}
          </MDTypography>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Slug */}
        <MDBox display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={0.5}>
          <MDTypography
            variant="caption"
            sx={{
              fontFamily: "monospace",
              bgcolor: lightDark("grey.100", DARK.surface),
              px: 1,
              py: 0.25,
              borderRadius: 1,
              flex: 1,
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={asset.slug}
          >
            {asset.slug}
          </MDTypography>
        </MDBox>

        {/* Meta */}
        <MDBox display="flex" justifyContent="space-between" mt={1}>
          <MDTypography variant="caption" color="secondary">
            {formatBytes(asset.size)}
          </MDTypography>
          <MDTypography variant="caption" color="secondary">
            {formatDate(asset.createdAt)}
          </MDTypography>
        </MDBox>
      </CardContent>

      <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 1.5, pt: 0.5 }}>
        {/* Copy template var */}
        <Tooltip
          title={copiedKey === `var-${asset.slug}` ? "Copied!" : `Copy {{file:${asset.slug}}}`}
          placement="top"
        >
          <MDButton
            size="small"
            variant="outlined"
            color={copiedKey === `var-${asset.slug}` ? "success" : "info"}
            onClick={() => onCopy(templateVar, `var-${asset.slug}`)}
            sx={[{ fontSize: "0.65rem", px: 1 }, phoneTapHeight]}
          >
            <Icon sx={{ mr: 0.5, fontSize: "0.9rem !important" }}>code</Icon>
            {"{​{var}​}"}
          </MDButton>
        </Tooltip>

        <MDBox display="flex" gap={0.5}>
          {/* Copy URL */}
          <Tooltip
            title={copiedKey === `url-${asset.slug}` ? "Copied!" : "Copy public URL"}
            placement="top"
          >
            <IconButton
              size="small"
              sx={phoneIconTap}
              onClick={() => onCopy(publicUrl, `url-${asset.slug}`)}
            >
              <Icon
                fontSize="small"
                color={copiedKey === `url-${asset.slug}` ? "success" : "inherit"}
              >
                link
              </Icon>
            </IconButton>
          </Tooltip>

          {/* Open in new tab */}
          <Tooltip title="Open file" placement="top">
            <IconButton
              size="small"
              sx={phoneIconTap}
              component="a"
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon fontSize="small">open_in_new</Icon>
            </IconButton>
          </Tooltip>

          {/* Delete */}
          <Tooltip title="Delete file" placement="top">
            <IconButton
              size="small"
              color="error"
              sx={phoneIconTap}
              onClick={() => onDelete(asset)}
            >
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      </CardActions>
    </Card>
  );
}

// ─── Upload dialog ────────────────────────────────────────────────────────────

function UploadDialog({ open, onClose, onUploaded }) {
  const [slug, setSlug] = useState("");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setSlug("");
    setLabel("");
    setFile(null);
    setUploading(false);
    setError("");
    setSlugTouched(false);
  };

  const handleFileChange = (e) => {
    const picked = e.target.files[0];
    if (!picked) return;
    setFile(picked);
    if (!slugTouched) {
      const ext =
        picked.name.lastIndexOf(".") > 0
          ? picked.name.slice(0, picked.name.lastIndexOf("."))
          : picked.name;
      setSlug(slugify(ext));
    }
    if (!label) setLabel(picked.name);
  };

  // While typing, keep a trailing hyphen/space (it is the start of the next word);
  // the slug is trimmed on blur and again when saving.
  const handleSlugChange = (e) => {
    setSlugTouched(true);
    setSlug(
      e.target.value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-/, "")
    );
  };
  const handleSlugBlur = () => setSlug((s) => slugify(s));

  const slugValid = /^[a-z0-9-]+$/.test(slug);

  const handleUpload = async () => {
    if (!file || !slug || !slugValid) return;
    const cleanSlug = slugify(slug);
    if (!cleanSlug) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("slug", cleanSlug);
      fd.append("label", label);
      const { asset } = await adminApi.uploadFileAsset(fd);
      onUploaded(asset);
      reset();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload File Asset</DialogTitle>
      <DialogContent>
        {error && (
          <MDAlert color="error" dismissible sx={{ mb: 2 }}>
            {error}
          </MDAlert>
        )}

        {/* File picker */}
        <MDBox
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: "2px dashed",
            borderColor: file ? "success.main" : "grey.400",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            mb: 3,
            "&:hover": { borderColor: "info.main", bgcolor: lightDark("grey.50", DARK.surface) },
          }}
        >
          <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} />
          <Icon
            sx={{
              fontSize: 40,
              color: file ? "success.main" : "grey.500",
              display: "block",
              mx: "auto",
              mb: 1,
            }}
          >
            {file ? "check_circle" : "cloud_upload"}
          </Icon>
          <MDTypography
            variant="body2"
            color={file ? "success" : "secondary"}
            sx={{ overflowWrap: "anywhere" }}
          >
            {file ? file.name : "Click to choose a file"}
          </MDTypography>
          {file && (
            <MDTypography variant="caption" color="secondary">
              {formatBytes(file.size)}
            </MDTypography>
          )}
        </MDBox>

        {/* Slug */}
        <TextField
          label="Slug (unique identifier)"
          value={slug}
          onChange={handleSlugChange}
          onBlur={handleSlugBlur}
          fullWidth
          required
          sx={{ mb: 2 }}
          helperText={
            slug && !slugValid
              ? "Lowercase letters, numbers, and hyphens only"
              : `Use as {{file:${slug || "your-slug"}}} in any email, WhatsApp, or SMS content`
          }
          error={slug.length > 0 && !slugValid}
          FormHelperTextProps={{ sx: { overflowWrap: "anywhere" } }}
          inputProps={{ style: { fontFamily: "monospace" } }}
        />

        {/* Label */}
        <TextField
          label="Label (optional)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          fullWidth
          helperText="Human-readable name shown in the file library"
        />
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleClose} disabled={uploading}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          onClick={handleUpload}
          disabled={!file || !slug || !slugValid || uploading}
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {uploading ? "Uploading…" : "Upload"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Delete confirm dialog ────────────────────────────────────────────────────

function DeleteDialog({ asset, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await adminApi.deleteFileAsset(asset.slug);
      onDeleted(asset.slug);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not delete the file. Nothing was removed.");
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!asset} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete File?</DialogTitle>
      <DialogContent>
        {error && (
          <MDAlert color="error" dismissible sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </MDAlert>
        )}
        <MDTypography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          This will permanently delete <strong>{asset?.label || asset?.originalFilename}</strong> (
          <code>file:{asset?.slug}</code>) from the server. Any template variable referencing this
          slug will silently produce an empty string.
        </MDTypography>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} disabled={deleting}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color="error" onClick={handleDelete} disabled={deleting}>
          {deleting ? "Deleting…" : "Delete"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function FileManager() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [notice, setNotice] = useState(null);
  const { copiedKey, copy } = useCopied();

  const showNotice = (msg, color = "success") => {
    setNotice({ msg, color });
    setTimeout(() => setNotice(null), 3000);
  };

  useEffect(() => {
    adminApi
      .listFileAssets()
      .then(({ files: f }) => setFiles(f || []))
      .catch(() => showNotice("Failed to load file assets", "error"))
      .finally(() => setLoading(false));
  }, []);

  const handleUploaded = (asset) => {
    setFiles((prev) => {
      const idx = prev.findIndex((f) => f.slug === asset.slug);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = asset;
        return updated;
      }
      return [asset, ...prev];
    });
    showNotice(`"${asset.label || asset.originalFilename}" uploaded — use {{file:${asset.slug}}}`);
  };

  const handleDeleted = (slug) => {
    setFiles((prev) => prev.filter((f) => f.slug !== slug));
    showNotice("File deleted");
  };

  const handleCopy = (text, key) => {
    copy(text, key);
    showNotice("Copied to clipboard");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={{ xs: 2, sm: 0 }}
          justifyContent="space-between"
          alignItems="flex-start"
          mb={3}
        >
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              File Library
            </MDTypography>
            <MDTypography variant="body2" color="secondary" mt={0.5}>
              Upload files and reference them anywhere using{" "}
              <MDBox component="code" color="inherit" sx={INLINE_CODE_SX}>
                {"{{file:slug}}"}
              </MDBox>{" "}
              — in email HTML, WhatsApp messages, or SMS content. Files are also served publicly at{" "}
              <MDBox component="code" color="inherit" sx={INLINE_CODE_SX}>
                /files/:slug
              </MDBox>
              .
            </MDTypography>
          </MDBox>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => setUploadOpen(true)}
            sx={{ ml: { xs: 0, sm: 2 }, flexShrink: 0 }}
          >
            <Icon sx={{ mr: 1 }}>upload_file</Icon>
            Upload File
          </MDButton>
        </MDBox>

        {/* Notice */}
        {notice && (
          <MDAlert color={notice.color} sx={{ mb: 2 }}>
            {notice.msg}
          </MDAlert>
        )}

        {/* Usage hint */}
        <Card sx={{ mb: 3, bgcolor: "info.light", border: "1px solid", borderColor: "info.main" }}>
          <CardContent sx={{ py: "12px !important" }}>
            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Icon color="info">lightbulb</Icon>
              <MDTypography variant="body2" fontWeight="medium">
                How to use:
              </MDTypography>
              <Chip
                label="{{file:slug}} → inline link"
                size="small"
                sx={[{ fontFamily: "monospace", fontSize: "0.7rem" }, phoneChipWrap]}
              />
              <Chip
                label="file:slug as attachment path → email attachment"
                size="small"
                sx={[{ fontFamily: "monospace", fontSize: "0.7rem" }, phoneChipWrap]}
              />
            </MDBox>
          </CardContent>
        </Card>

        {/* Content */}
        {loading ? (
          <MDBox sx={{ width: "100%" }}>
            <LinearProgress color="info" />
          </MDBox>
        ) : files.length === 0 ? (
          <Card>
            <CardContent>
              <MDBox py={6} textAlign="center">
                <Icon sx={{ fontSize: 56, color: "grey.400", display: "block", mx: "auto", mb: 2 }}>
                  cloud_upload
                </Icon>
                <MDTypography variant="h6" color="secondary">
                  No files uploaded yet
                </MDTypography>
                <MDTypography variant="body2" color="secondary" mt={1} mb={3}>
                  Upload your first file to get a reusable slug you can embed anywhere.
                </MDTypography>
                <MDButton variant="gradient" color="info" onClick={() => setUploadOpen(true)}>
                  Upload your first file
                </MDButton>
              </MDBox>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {files.map((asset) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={asset.slug}>
                <FileCard
                  asset={asset}
                  onDelete={setDeleteTarget}
                  onCopy={handleCopy}
                  copiedKey={copiedKey}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </MDBox>

      <Footer />

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={handleUploaded}
      />

      <DeleteDialog
        asset={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />
    </DashboardLayout>
  );
}
